import React, { useCallback, useRef } from "react";

import { useStorefrontConfig } from "../types/storefront";
import { AddressEntry } from "./address_entry";
import { FulfillmentTimeSelector } from "./fulfillment_time_selector";
import { ReactComponent as AnimatedScooter } from "images/animated/animated_scooter.svg?react";
import { Popover } from "../../components/popover";
import { StreamlineIcon } from "src/components/icons";
import arrowRightIconUrl from "images/icons/arrow-right.svg";
import informationCircleIconUrl from "images/icons/information-circle.svg";
import pencilIconUrl from "images/icons/pencil.svg";
import pinIconUrl from "images/icons/pin.svg";
import timeClockCircleIconUrl from "images/icons/time-clock-circle.svg";

const FulfillmentPopover = ({ hasCompactTrigger }: { hasCompactTrigger?: boolean }) : React.JSX.Element => {
  const storefrontConfig = useStorefrontConfig();
  const addrForm = useRef<HTMLFormElement | null>(null);
  const fulfilTypeForm = useRef<HTMLFormElement | null>(null);

  const changeFulfillment = useCallback(() => {
    fulfilTypeForm.current?.submit();
  }, [fulfilTypeForm]);

  const unexpandedContent = (showPopover: () => void) => {
    if (hasCompactTrigger) {
      return (<div className="option-bar is-tiny">
        <button onClick={showPopover} className="link-selector is-aligned-right" aria-label="Change order time and address" data-cy="open-fulfillment-options">
          <span>
            <span>{storefrontConfig.storeInfo.cartFulfillmentTimeEstimate}</span>
            <StreamlineIcon href={pencilIconUrl} />
          </span>
        </button>
      </div>);
    } else {
      return (<div className="option-bar is-tiny">
        <button onClick={showPopover} className="link-selector" aria-label="Change order time" data-cy="open-fulfillment-options">
          <span>
            <StreamlineIcon href={timeClockCircleIconUrl} />
            <span>{storefrontConfig.storeInfo.cartFulfillmentTimeEstimate}</span>
          </span>
        </button>

        <StreamlineIcon href={arrowRightIconUrl} />

        <button onClick={showPopover} className="link-selector will-shrink" aria-label="Change address" data-cy="open-fulfillment-options">
          <span>
            <StreamlineIcon href={pinIconUrl} />
            <span>{storefrontConfig.currentAddress}</span>
            <StreamlineIcon href={pencilIconUrl} />
          </span>
        </button>
      </div>);
    }
  };

  const expandedContent = (hidePopover: () => void) => {
    return (
      <>
        <section>
          <form action="/restaurants/change_delivery_type" acceptCharset="UTF-8" method="POST" ref={fulfilTypeForm} >
            <input type="hidden" name="storefront_id" value={storefrontConfig.storefrontId} />
            <input type="hidden" name="redirect_to" value={window.location.pathname} />
            <input type="hidden" name="authenticity_token" value={storefrontConfig.csrfToken} autoComplete="off" />

            <div className="chip is-choice">
              <input type="radio" id="fulfillment_method_delivery_for_storefront" onClick={changeFulfillment} readOnly checked={storefrontConfig.fulfillmentMethod == "delivery"} />
              <label htmlFor="fulfillment_method_delivery_for_storefront" data-cy="fulfillment_method_delivery_for_storefront">Delivery</label>
            </div>

            <div className="chip is-choice">
              <input type="radio" id="fulfillment_method_pickup_for_storefront" onClick={changeFulfillment} readOnly checked={storefrontConfig.fulfillmentMethod == "pickup"} />
              <label htmlFor="fulfillment_method_pickup_for_storefront" data-cy="fulfillment_method_pickup_for_storefront">Pickup</label>
            </div>
          </form>
        </section>

        <hr className="space-mt-none space-mb-none" />

        <section>
          <div className="notice is-important space-mb-md is-completely-hidden">
            <StreamlineIcon href={informationCircleIconUrl} /> <span data-address-entry-target="addressErrorText"></span>
          </div>

          <form ref={addrForm} data-address-entry-target="form" action="/restaurants/change_delivery_details" acceptCharset="UTF-8" method="post">
            <input type="hidden" name="storefront_id" value={storefrontConfig.storefrontId} />
            <input type="hidden" name="authenticity_token" value={storefrontConfig.csrfToken} autoComplete="off" />
            <input value={window.location.pathname} autoComplete="off" type="hidden" name="redirect_to" id="redirect_to" />

            <AddressEntry currentAddress={storefrontConfig.currentAddress} />

            <FulfillmentTimeSelector
              selectedTime={storefrontConfig.preferredTime}
              fulfillmentMethod={storefrontConfig.fulfillmentMethod}
              fulfillmentTimeOptions={storefrontConfig.storeInfo.fulfillmentTimeOptions}
            />

          </form>
        </section>

        <section className="is-flexbox flex-column flex-grow-1 justify-content-end">
          <button type="submit" className="button is-primary is-full-width" data-action="address-entry#buttonPressed" data-address-entry-target="applyButton" data-cy="apply-delivery-details">
            <span className="icon custom-icon is-medium show-while-loading">
              <AnimatedScooter className="animated-scooter" />
            </span>
            <span className="show-while-loading">Please wait...</span>
            <span className="hide-while-loading">Apply</span>
          </button>

          <button type="button" className="button space-mt-sm is-hidden-above-desktop" aria-label="Cancel" onClick={hidePopover}>
            Cancel
          </button>
        </section>
      </>
    );
  };

  return (
    <Popover
      unexpandedContent={unexpandedContent}
      expandedContent={expandedContent}
      options={{ isFullHeightOnMobile: true, extraClasses: ["has-fulfillment-options"] }}
      extraAttrs={{ "data-controller": "address-entry", "data-address-entry-autosubmit": "false" }}
    />
  );
}

export { FulfillmentPopover };
